import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrollToId, EASE } from "@/components/Reveal";
import Logo from "@/components/Logo";

const LINKS = [
  { label: "Process", href: "#process" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Vacancies", href: "/vacancies" },
  { label: "Stories", href: "#stories" },
  { label: "FAQ", href: "#faq" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navigate = useNavigate();

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/")) {
      navigate(href);
      return;
    }
    scrollToId(href);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-500 ${
        scrolled
          ? "bg-gradient-to-r from-[#0a1730]/95 via-[#0c1e40]/90 to-[#2b1503]/90 backdrop-blur-xl border-white/10"
          : "bg-gradient-to-r from-[#0a1730]/50 via-transparent to-[#2b1503]/30 border-transparent"
      }`}
      data-testid="navbar"
    >
      <div
        className="h-[3px] w-full bg-gradient-to-r from-[#1e5aa8] via-[#3f9be0] to-[#f06423]"
        data-testid="nav-gradient-strip"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <button
          onClick={() => window.__lenis?.scrollTo(0)}
          data-testid="nav-logo"
          aria-label="HR99 Global Services — back to top"
        >
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              onClick={() => go(l.href)}
              className="text-sm text-zinc-400 hover:text-white transition-colors duration-300 tracking-wide"
              data-testid={`nav-link-${l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => go("#contact")}
            className="hidden md:inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-orange-400 transition-colors duration-300"
            data-testid="nav-cta-button"
          >
            Book Free Call <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            className="md:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="md:hidden overflow-hidden bg-zinc-950/95 backdrop-blur-xl border-t border-white/10"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  onClick={() => go(l.href)}
                  className="text-left text-lg text-zinc-300 hover:text-primary transition-colors"
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => go("#contact")}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-5 py-3 font-semibold"
                data-testid="nav-mobile-cta"
              >
                Book Free Call <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
